import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListAuditLog, ListAuditLogEventTypes } from '../../store/AuditLogAction';
import debounce from 'lodash/debounce';
import { Row, Col, Button } from 'reactstrap';
import * as Helper from '../../util/Helper';
import SelectInput from '../misc/SelectInput';
import { Link, useSearchParams } from 'react-router-dom';
import StatusCode from '../../util/StatusCode';
import ModalComponent from '../misc/ModalComponent';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Search from '../misc/Search';
import FilterBottomComponent from '../misc/FilterBottomComponent';
import { useTranslation } from 'react-i18next';
import { CoreLoggedInUser } from '../../models/CoreLoggedInUser';
import { AuditLogActorType, AuditLogEvent, CoreAuditLogActor, CoreAuditLogEntry, CoreAuditLogEvent, CoreAuditLogSource } from '../../models/CoreAuditLogEntry';
import { PageResult } from '../../models/CoreModels';
import { CacheRequestState, RequestState } from '../../models/RequestState';
import RequestParser from '../../util/RequestParser';
import { RootState } from '../../store/store';
import { FlowSyncAccessPolicy, isAuthorized } from '../../models/FlowSyncAccessPolicy';


const AuditLog = () => {
    const { t } = useTranslation(['common', 'configuration']);

    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [search, setSearch] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [additionalInformation, setAdditionalInformation] = useState<string | undefined>(undefined);

    const logs = useSelector<RootState, CacheRequestState<PageResult<CoreAuditLogEntry>>>(state => state.auditlog.auditLogs);
    const eventTypes = useSelector<RootState, RequestState<AuditLogEvent[]>>(state => state.auditlog.eventTypes);
    const me = useSelector<RootState, CoreLoggedInUser | undefined>(state => state.me.me);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasAuditLogPermission = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.AUDIT_LOG_READ);
    
    const filter = RequestParser.parseAuditLogSearchPageRequest(searchParams);

    useEffect(() => {
        if (filter.search) {
            setSearch(filter.search);
        }
        ListAuditLogEventTypes()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        ListAuditLog(filter, true)(dispatch);
        window.scrollTo(0, 0);
    }, [searchParams]);

    useEffect(() => {
        const interval = setInterval(() => {
            ListAuditLog(filter, false)(dispatch);
        }, 60000)
        return () => clearInterval(interval);
    }, []);

    const updateFilter = (name: string, value: number | string | undefined) => {
        if (name !== 'page') {
            searchParams.set('page', "1");
        }

        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        }
        else {
            searchParams.delete(name);
        }
        setSearchParams(searchParams);
    }

    const debouncedSearch = debounce((search?: string) => updateFilter('search', search), 300);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        let searchValue = e.target.value;
        setSearch(searchValue);
        debouncedSearch(searchValue);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const sourceName = (log: CoreAuditLogSource | undefined, event: CoreAuditLogEvent | undefined) => {
        const masterUrl = me?.masterUrl;
        const customerId = me?.customerId;

        if (!log || !event) {
            return;
        }

        switch (log.type) {
            case 1: {
                if (event.eventId === 10003) {
                    return <Link to={{ pathname: `/module/${log.id}/status` }} target="_blank">{log.name}</Link>
                }
                return <Link to={{ pathname: `/module/${log.id}/configuration` }} target="_blank">{log.name}</Link>
            }
            case 3: {
                return <a href={`${masterUrl}/license/${customerId}`} target="_blank" rel="noreferrer">{log.name}</a>
            }
            case 4: {
                return <a href={`${masterUrl}/permissiongroups`} target="_blank" rel="noreferrer">{log.name}</a>
            }
            case 5: {
                return <a href={`${masterUrl}/permissions/${customerId}`} target="_blank" rel="noreferrer">{log.name}</a>
            }
            case 7: {
                return <Link to={{ pathname: `/user/${log.id}` }} target="_blank">{log.name}</Link>
            }
            case 8: {
                return <Link to={{ pathname: `/instanceConfig` }} target="_blank">{
                    t('instanceSettings.title', { defaultValue: log.name, ns: 'configuration' })
                }</Link>
            }
            case 9: {
                return <Link to={{ pathname: `/activity/${log.id}` }} target="_blank">{log.name}</Link>
            }
            case 10: {
                return <Link to={{ pathname: `/group/${log.id}` }} target="_blank">{log.name}</Link>
            }
            case 12: {
                return <Link to={{ pathname: `/passwordadmin/profiles` }} target="_blank">{log.name}</Link>
            }
            case 13: {
                return <Link to={{ pathname: `/device/${log.id}/information` }} target="_blank">{log.name}</Link>
            }
            case 14: {
                return <Link to={{ pathname: `/classroomadmin/classroom/${log.id}/information` }} target="_blank">{log.name}</Link>
            }
            default: return log.name
        }
    }

    const renderActor = (actor: CoreAuditLogActor) => {
        if (actor.type === AuditLogActorType.CONSUMER) {
            return t('auditLog.apiActor', {
                ns: 'configuration',
                consumer: actor.displayName || '-',
                clientId: actor.uniqueIdentifier || '-'
            });
        }

        return `${actor.displayName || '-'}, ${actor.email || actor.uniqueIdentifier || '-'}`;
    }

    const renderAuditLogs = () => {
        let listAuditLogs: InfoObject[] = [];
        if (logs.code === StatusCode.COMPLETE || (logs.code === StatusCode.PENDING && logs.data)) {
            if (!logs.data) {
                return;
            };
            listAuditLogs = logs.data.values.map(log => ({
                id: log.id,
                data: [
                    { value: log.event?.eventId ? t(`auditLogEvent.${log.event.eventId}`, { ns: 'configuration' }) : '-' },
                    { value: sourceName(log?.source, log?.event) },
                    { value: log?.source && log.source.type ? t(`auditLog.sourceTypes.${log.source.type}`, { ns: 'configuration' }) : '-' },
                    { value: renderActor(log.actor) },
                    { value: Helper.formatDate(log.timestamp) },
                    { value: log?.additionalInformation && <Button className="icon-btn right" onClick={() => (setOpen(true), setAdditionalInformation(log.additionalInformation))} title={t('auditLog.showAdvancedInfo', { ns: 'configuration' })}><FontAwesomeIcon icon="ellipsis-vertical" /></Button> }
                ]
            }));
        };

        return (
            <div>
                <TableHorizontal
                    header={[{ value: t('auditLog.auditLogListHeaders.description', { ns: 'configuration' }) }, { value: t('auditLog.auditLogListHeaders.title', { ns: 'configuration' }) }, { value: t('auditLog.auditLogListHeaders.type', { ns: 'configuration' }) }, { value: t('auditLog.auditLogListHeaders.user', { ns: 'configuration' }) }, { value: t('auditLog.auditLogListHeaders.timestamp', { ns: 'configuration' }) }, { value: t('auditLog.auditLogListHeaders.advanced', { ns: 'configuration' }) }]}
                    info={listAuditLogs}
                    error={logs.code === StatusCode.ERROR}
                    searching={logs.code === StatusCode.PENDING}
                    displayName={t('auditLog.title', { ns: 'configuration' })}
                    noResMsg={t('auditLog.notFound', { ns: 'configuration' })}
                />
            </div>
        )
    }

    const renderFilter = () => {
        return (
            <Row>
                <Col sm="6">
                    <Search value={search} onChange={onSearch} placeholder={t('auditLog.searchAuditLog', { ns: 'configuration' })} />
                </Col>
                <Col sm="6">
                    <SelectInput
                        items={eventTypes.code === StatusCode.COMPLETE ? eventTypes.data.map(e => ({ value: e.eventId, text: `${t('filter.on', { ns: 'common' })} ${t(`auditLogEvent.${e.eventId}`, { ns: 'configuration' })}` })) : []}
                        onChange={(name, value) => updateFilter(name, value)}
                        name='eventId'
                        selected={filter.eventId}
                        defaultText={t('auditLog.showAllDescriptions', { ns: 'configuration' })}
                        disabled={eventTypes.code !== StatusCode.COMPLETE}
                    />
                </Col>
            </Row>
        )
    }

    const renderBody = () => {
        if (!hasAuditLogPermission) {
            return t('auditLog.unauthorized', { ns: 'configuration' })
        } else {
            return (<>

                {renderAuditLogs()}

                {open && <ModalComponent
                    isOpen={open}
                    toggleModal={() => (setOpen(false), setAdditionalInformation(undefined))}
                    header={t('auditLog.auditLogListHeaders.advanced', { ns: 'configuration' })}
                    size="xl"
                    cancel={t('action.close', { ns: 'common' })}
                >
                    <div>
                        <pre>{Helper.formatText(additionalInformation)}</pre>
                    </div>
                </ModalComponent>}
            </>)
        }
    }


    return (
        <div className="container auditlog">
            <div className="header--fixed">
                <h1>{t('auditLog.title', { ns: 'configuration' })}</h1>
                {renderFilter()}
            </div>
            <div className="body--scroll padding-40">
                {renderBody()}
            </div>

            <FilterBottomComponent
                clearFilter={() => clearFilter()}
                updateFilter={(page) => updateFilter("page", page)}
                requestState={logs}
            />

        </div>
    );
}

export default AuditLog;
